import { API_BASE_URL, USE_FEDERATED_AUTH } from 'config';
import { Login, LoginContext, LoginUserDetailDto, LoginUserListItemDto } from '../types';

export type Role = {
  id: number;
  name: string;
};

export type Module = {
  id?: number;
  name?: string;
  key?: number;
};

export type OrganizationSettings = {
  [key: string]: unknown;
};

export type UserDto = {
  id: string;
  name: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  avatarColor: string;
  organizationId: number;
  organizationName: string;
  organizationTypeId: number;
  organizationIsAutoStyleNumberOn: boolean;
  organizationIsUniqueStyleNumber: boolean;
  organizationIsStyleNumberGeneratorActive: boolean;
  roles: Array<Role>;
  modules: Array<Module>;
  organizationSettings: OrganizationSettings | null;
};

const mapUserDtoToLoginContext = (user: UserDto): Login => {
  if (!user) {
    return null;
  }
  const loginContext: LoginContext = {
    id: user.id,
    name: user.name,
    organizationType: user.organizationTypeId,
    organizationName: user.organizationName,
    email: user.email,
    roleNames: user.roles.map(role => role.name),
    designerOrganizationName: user.organizationName,
    designerOrganizationId: 0,
    brands: undefined,
    organizationId: user.organizationId,
    signUpDate: undefined,
    planType: undefined,
    planTier: undefined,
    platformNewsletterSubscription: undefined,
    inviteCode: undefined,
  };
  return {
    loginContext,
    user: null,
    token: null,
  };
};

// Maps organization type string from /api/login to the numeric ID used in legacy LoginContext.
// designer = 1, supplier = 2 (matches OrganizationTypeId in DesignHub.Domain)
const ORG_TYPE_ID: Record<string, number> = { designer: 1, supplier: 2 };

const mapLoginUserDetailToLogin = (user: LoginUserDetailDto): Login => {
  if (!user) return null;
  const loginContext: LoginContext = {
    id: user.id,
    name: user.name,
    organizationType: ORG_TYPE_ID[user.organization.type] ?? 0,
    organizationName: user.organization.name,
    email: user.email,
    roleNames: user.roles.map(role => role.name),
    designerOrganizationName: user.organization.name,
    designerOrganizationId: user.organization.id,
    brands: undefined,
    organizationId: user.organization.id,
    signUpDate: undefined,
    planType: undefined,
    planTier: undefined,
    platformNewsletterSubscription: undefined,
    inviteCode: undefined,
  };
  return { loginContext, user: null, token: null };
};

export const fetchLoginUsers = async (accessToken: string): Promise<LoginUserListItemDto[]> => {
  if (!accessToken) return [];
  const response = await fetch(`${API_BASE_URL}/api/login`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Failed to fetch login users: ${response.status} ${errorText}`);
  }
  return response.json();
};

export const fetchCurrentLoginUser = async (
  accessToken: string,
  userId: string
): Promise<Login> => {
  if (!accessToken || !userId) return null;
  const response = await fetch(`${API_BASE_URL}/api/login/user`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId,
    },
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Failed to fetch current user: ${response.status} ${errorText}`);
  }
  const data: LoginUserDetailDto = await response.json();
  return mapLoginUserDetailToLogin(data);
};

export const fetchLogin = async (accessToken: string): Promise<Login> => {
  if (!(accessToken && USE_FEDERATED_AUTH)) {
    return {};
  }
  const response = await fetch(`${API_BASE_URL}/api/user`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Failed to fetch user: ${response.status} ${response.statusText} ${errorText}`);
  }

  const data: UserDto = await response.json();
  return mapUserDtoToLoginContext(data);
};
